"use client";

import { CheckCircle2 } from "lucide-react";
import Image from "next/image";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { useNewsletterSubscribe } from "./use-newsletter-subscribe";
import { useSubscriptionStatus } from "./use-subscription-status";

interface MobileInlineNewsletterProps {
  className?: string;
}

export function MobileInlineNewsletter({
  className,
}: MobileInlineNewsletterProps) {
  const {
    email,
    setEmail,
    handleSubmit,
    isSubmitting,
    isError,
    state,
    subscribeToEdition,
  } = useNewsletterSubscribe({ source: "mobile_inline" });

  const { subscribedToCurrent, subscribedToOther, otherEdition, otherLabel } =
    useSubscriptionStatus();

  const [upsellSubmitting, setUpsellSubmitting] = useState(false);
  const [upsellDone, setUpsellDone] = useState(false);

  const justSubscribed = state.status === "success";

  // Already subscribed (returning user) — hide
  if (subscribedToCurrent && !justSubscribed) {
    return null;
  }
  // Both done — hide
  if (upsellDone) {
    return null;
  }

  const handleUpsell = async () => {
    setUpsellSubmitting(true);
    const ok = await subscribeToEdition({
      edition: otherEdition,
      includeEditions: ["engineer", "creative"],
    });
    setUpsellSubmitting(false);
    if (ok) {
      setUpsellDone(true);
    }
  };

  return (
    <section
      className={cn("lg:hidden", className)}
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
    >
      <div className="rounded-2xl bg-[var(--explore-bg-card)] p-6 text-center shadow-[var(--explore-inline-shadow)]">
        <div className="mx-auto mb-3 h-14 w-14 overflow-hidden rounded-full">
          <Image
            alt="AI Primer mascot"
            className="h-full w-full object-cover"
            height={56}
            src="/logo.webp"
            width={56}
          />
        </div>

        {/* ── Form ── */}
        {!justSubscribed && (
          <>
            <h3 className="mb-1 font-semibold text-[1rem] text-[var(--explore-text-primary)]">
              Daily AI Digest
            </h3>
            <p className="mb-4 text-[0.8rem] text-[var(--explore-text-muted)]">
              Get the best stories delivered
              <br />
              to your inbox
            </p>
            <form
              className="mx-auto flex max-w-[360px] flex-col gap-2"
              onSubmit={handleSubmit}
            >
              <input
                className="w-full rounded-full border border-[var(--explore-border)] bg-[var(--color-pure-white)] px-4 py-2.5 text-center text-[0.85rem] outline-none focus:border-[var(--explore-accent-secondary)]"
                disabled={isSubmitting}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                type="email"
                value={email}
              />
              {isError && (
                <p className="text-[0.75rem] text-[var(--explore-error-text)]">
                  {state.message}
                </p>
              )}
              <button
                className="rounded-full bg-[var(--explore-accent-yellow)] px-5 py-2.5 font-semibold text-[0.85rem] text-[var(--explore-on-accent-text)] transition-transform duration-150 active:scale-95"
                type="submit"
              >
                {isSubmitting ? "Subscribing..." : "Subscribe"}
              </button>
            </form>
          </>
        )}

        {/* ── Upsell ── */}
        {justSubscribed && !subscribedToOther && (
          <>
            <div className="mb-3 flex items-center justify-center gap-1.5">
              <CheckCircle2 className="h-5 w-5 text-[var(--explore-success-text)]" />
              <span className="font-semibold text-[0.9rem] text-[var(--explore-success-text)]">
                Subscribed!
              </span>
            </div>
            <p className="mb-3 text-[0.8rem] text-[var(--explore-text-muted)]">
              Want to also get the {otherLabel} digest?
            </p>
            <button
              className="rounded-full bg-[var(--explore-accent-yellow)] px-5 py-2.5 font-semibold text-[0.85rem] text-[var(--explore-on-accent-text)] transition-transform duration-150 active:scale-95"
              onClick={handleUpsell}
              type="button"
            >
              {upsellSubmitting ? "Adding..." : `Yes, add ${otherLabel} too`}
            </button>
          </>
        )}

        {/* ── Done (both subscribed) ── */}
        {justSubscribed && subscribedToOther && (
          <div className="flex items-center justify-center gap-1.5 py-2">
            <CheckCircle2 className="h-5 w-5 text-[var(--explore-success-text)]" />
            <span className="font-semibold text-[0.9rem] text-[var(--explore-success-text)]">
              You're all set!
            </span>
          </div>
        )}
      </div>
    </section>
  );
}
